"use client";

import { PricingPlan } from "@/components/pricing-plan";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useCustomSession } from "@/hooks";
import { ArrowRight, CheckCircle, Server, User } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { TbBrandDiscord, TbBrandGithub } from "react-icons/tb";

const API_BASE = (process.env.NEXT_PUBLIC_API_BASE || "/api").replace(/\/$/, "");

export default function LandingPage() {
	const [showModal, setShowModal] = useState(false);
	const { session, loading } = useCustomSession();
	const [apiError, setApiError] = useState(false);
	const [apiReason, setApiReason] = useState<string | null>(null);
	const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
	const [maintenanceMessage, setMaintenanceMessage] = useState<string | null>(null);

	// Check if backend API is accessible
	useEffect(() => {
		const checkAPI = async () => {
			try {
				const response = await fetch(`${API_BASE}/health`);
				if (!response.ok) {
					setApiError(true);
					setApiReason("unknown_error");
					return;
				}
				const data = await response.json();
				setApiError(!data.online);
				setApiReason(data.reason ?? null);
			} catch (error) {
				console.error("API health check failed:", error);
				setApiError(true);
				setApiReason("api_unreachable");
			}
		};
		checkAPI();
	}, []);

	useEffect(() => {
		const fetchMaintenance = async () => {
			try {
				const response = await fetch(`${API_BASE}/system/maintenance`, { credentials: "include" });
				if (!response.ok) return;
				const data = await response.json();
				setMaintenanceEnabled(Boolean(data?.enabled));
				setMaintenanceMessage(data?.message || null);
			} catch (error) {
				console.error("Failed to fetch maintenance status:", error);
			}
		};

		fetchMaintenance();
	}, []);

	// Show loading state while checking authentication
	if (loading) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center font-sans">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
					<p className="mt-4 text-muted-foreground">Loading...</p>
				</div>
			</div>
		);
	}

	const statusLabel = apiError ? "OFFLINE" : "READY";
	const statusDetail = !apiError ? "All systems operational." : apiReason === "maintenance" ? "Backend in maintenance mode." : apiReason === "degraded" ? "Backend responding with errors." : "Backend API unreachable.";

	return (
		<div className="min-h-screen bg-background">
			{maintenanceEnabled && (
				<div className="w-full bg-amber-500/15 border-b border-amber-500/30">
					<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2 text-sm text-amber-700 dark:text-amber-300">
						<strong className="mr-2">Maintenance Mode:</strong>
						{maintenanceMessage || "Some features may be temporarily unavailable while maintenance is in progress."}
					</div>
				</div>
			)}

			{/* Header */}
			<header className="border-b">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex h-16 items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
								<Image src="/images/Logo.svg" alt="LinkNSync" width={20} height={20} className="w-5 h-5 brightness-0 invert" />
							</div>
							<span className="text-xl font-semibold">LinkNSync</span>
						</div>
						<nav className="flex items-center gap-4">
							{session ? (
								<Link href="/dashboard">
									<Button>Dashboard</Button>
								</Link>
							) : (
								<>
									<Button onClick={() => !apiError && setShowModal(true)} disabled={apiError}>
										{apiError ? "System Offline" : "Get Started"}
									</Button>
								</>
							)}
						</nav>
					</div>
				</div>
			</header>

			{/* Main Content */}
			<main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
				{/* Hero Section */}
				<section className="text-center py-8">
					<div className="max-w-4xl mx-auto">
						<div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
							<Image src="/images/Logo.svg" alt="LinkNSync" width={32} height={32} className="w-8 h-8 brightness-0 invert" />
						</div>
						<h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">LinkNSync</h1>
						<p className="text-xl text-muted-foreground mb-2">Whitelisting Made Simple</p>
						<p className="text-muted-foreground max-w-2xl mx-auto mb-8">A powerful whitelisting plugin for Minecraft using Twitch and other platforms. Makes subscriber-only servers easy to use and manage.</p>
						<div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
							<Button size="lg" onClick={() => !apiError && setShowModal(true)} disabled={apiError} className="text-base px-8">
								{apiError ? "System Offline" : "Get Started"}
								<ArrowRight className="ml-2 h-4 w-4" />
							</Button>
							{session && (
								<Link href="/dashboard">
									<Button size="lg" variant="outline" className="text-base px-8">
										Go to Dashboard
									</Button>
								</Link>
							)}
						</div>
						<div className="mt-6 text-sm text-muted-foreground">
							<span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${apiError ? "border-destructive/20 text-destructive" : "border-green-500/20 text-green-600"}`}>
								<div className={`w-2 h-2 rounded-full ${apiError ? "bg-destructive" : "bg-green-500"} animate-pulse`}></div>
								{statusLabel} • {statusDetail}
							</span>
						</div>
					</div>
				</section>

				<Separator className="my-8" />

				{/* Pricing Section */}
				<section className="py-8">
					<PricingPlan
						heading="Choose Your Plan"
						description="Select the perfect plan for your needs"
						plans={[
							{
								id: "free",
								name: "Free",
								description: "Perfect for getting started",
								monthlyPrice: 0,
								yearlyPrice: 0,
								features: [{ text: "Basic server management" }, { text: "Up to 3 servers" }, { text: "Community support" }],
								button: {
									text: "Get Started",
									url: "#get-started",
									variant: "outline",
								},
							},
							{
								id: "pro",
								name: "Pro",
								description: "For serious server owners",
								monthlyPrice: 15,
								yearlyPrice: 120,
								features: [{ text: "Advanced server management" }, { text: "Unlimited servers" }, { text: "Priority support" }, { text: "Advanced analytics" }],
								button: {
									text: "Upgrade to Pro",
									url: "#upgrade-pro",
								},
								popular: true,
								discount: 17,
							},
						]}
					/>
				</section>

				<Separator className="my-8" />

				{/* Features Section */}
				<section className="py-8">
					<div className="text-center mb-12">
						<h2 className="text-3xl font-bold mb-4">Powerful Features</h2>
						<p className="text-muted-foreground text-lg">Everything you need to manage your Minecraft server</p>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
						<Card className="text-center">
							<CardHeader>
								<div className="w-12 h-12 mx-auto mb-4 bg-blue-100 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
									<Server className="w-6 h-6 text-blue-600 dark:text-blue-400" />
								</div>
								<CardTitle>Server Management</CardTitle>
								<CardDescription>Easily manage multiple Minecraft servers with intuitive controls</CardDescription>
							</CardHeader>
						</Card>

						<Card className="text-center">
							<CardHeader>
								<div className="w-12 h-12 mx-auto mb-4 bg-purple-100 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
									<User className="w-6 h-6 text-purple-600 dark:text-purple-400" />
								</div>
								<CardTitle>User Integration</CardTitle>
								<CardDescription>Seamless integration with Twitch, YouTube, and other platforms</CardDescription>
							</CardHeader>
						</Card>

						<Card className="text-center">
							<CardHeader>
								<div className="w-12 h-12 mx-auto mb-4 bg-green-100 dark:bg-green-900/20 rounded-xl flex items-center justify-center">
									<CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
								</div>
								<CardTitle>Automated Whitelisting</CardTitle>
								<CardDescription>Automatic whitelist management based on subscriber status</CardDescription>
							</CardHeader>
						</Card>
					</div>
				</section>

				<Separator className="my-8" />

				{/* Community Section */}
				<section className="py-8">
					<Card className="max-w-4xl mx-auto">
						<CardHeader className="text-center">
							<CardTitle className="text-2xl">Join Our Community</CardTitle>
							<CardDescription>Connect with other server owners and get support</CardDescription>
						</CardHeader>
						<CardContent className="text-center">
							<div className="flex flex-col sm:flex-row gap-4 justify-center">
								<Button variant="outline" asChild>
									<a href="https://discord.gg/TBmrqhJsa9" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
										<TbBrandDiscord className="h-4 w-4" />
										Discord Server
									</a>
								</Button>
								<Button variant="outline" asChild>
									<a href="https://github.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
										<TbBrandGithub className="h-4 w-4" />
										GitHub
									</a>
								</Button>
							</div>
						</CardContent>
					</Card>
				</section>
			</main>

			{/* Footer */}
			<footer className="border-t mt-16">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<div className="text-center text-muted-foreground text-sm">
						© 2026 LinkNSync • System Active •{" "}
						<Link href="/privacy" className="text-primary hover:text-primary/80 underline">
							Privacy Policy
						</Link>{" "}
						•{" "}
						<Link href="/terms" className="text-primary hover:text-primary/80 underline">
							Terms of Service
						</Link>
					</div>
				</div>
			</footer>

			{/* Role Modal */}
			{showModal && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
					<div className="bg-background border rounded-xl shadow-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
						<h2 className="text-2xl font-bold mb-2">Choose Your Role</h2>
						<p className="text-muted-foreground mb-6">Select how you want to interact with LinkNSync</p>

						<div className="space-y-4">
							<Link href="/login?role=player" className="block">
								<Card className="hover:bg-accent transition-colors cursor-pointer">
									<CardContent className="p-4">
										<div className="flex items-center gap-4">
											<div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
												<User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
											</div>
											<div>
												<div className="font-medium">Player</div>
												<div className="text-sm text-muted-foreground">Join servers and get whitelisted</div>
											</div>
										</div>
									</CardContent>
								</Card>
							</Link>

							<Link href="/login?role=owner" className="block">
								<Card className="hover:bg-accent transition-colors cursor-pointer">
									<CardContent className="p-4">
										<div className="flex items-center gap-4">
											<div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
												<Server className="w-5 h-5 text-purple-600 dark:text-purple-400" />
											</div>
											<div>
												<div className="font-medium">Server Owner</div>
												<div className="text-sm text-muted-foreground">Manage servers and permissions</div>
											</div>
										</div>
									</CardContent>
								</Card>
							</Link>
						</div>

						<div className="mt-6 text-center">
							<Button variant="ghost" onClick={() => setShowModal(false)}>
								Cancel
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
